import React, { useContext, useMemo } from 'react';
import { renderRoutes } from 'react-router-config';

import { LinearProgress } from '@mui/material';
import Forbidden403Page from 'app/pages/errors/403/Forbidden403Page';
import ConfirmationDialog from 'app/shared/components/dialogs/ConfirmationDialog';
import Dialogs from 'app/shared/components/dialogs/Dialogs';
import PropTypes from 'prop-types';

import MobileTabsContext from '../MobileTabsProvider/MobileTabsContext';

import KeepAlivePane from './components/KeepAlivePane';

/**
 * Native bottom-nav content area: one kept-alive `KeepAlivePane` per visited tab. The single
 * route tree is rendered into each pane against that pane's (frozen/live) router context, so each
 * tab keeps its own mounted navigation stack. Dialog hosts render once for the whole area (not per
 * pane). Falls back to a plain route render outside a `MobileTabsProvider`.
 *
 * @param {Object} props
 * @param {Array} props.routes - the app route config
 * @param {boolean} props.isAccessGranted - whether the active route is permitted
 * @param {boolean} [props.isResolvingPermissions] - permissions still loading; render loading, not a verdict
 * @param {boolean} [props.reserveBottom] - reserve space for the floating bottom nav
 */
const MobileTabPanes = ({
  routes,
  isAccessGranted,
  isResolvingPermissions = false,
  reserveBottom = false,
}) => {
  const tabs = useContext(MobileTabsContext);

  const routesElement = useMemo(() => renderRoutes(routes), [routes]);

  if (!tabs) {
    return routesElement;
  }

  const { panes, activeTabId } = tabs;

  const renderPaneContent = isActive => {
    if (isActive && isResolvingPermissions) {
      return <LinearProgress className="visible" color="primary" />;
    }

    if (isActive && !isAccessGranted) {
      return <Forbidden403Page />;
    }

    return routesElement;
  };

  return (
    <>
      <Dialogs />
      <ConfirmationDialog />
      {panes.map(pane => {
        const isActive = pane.tabId === activeTabId;

        return (
          <KeepAlivePane
            isActive={isActive}
            key={pane.tabId}
            location={pane.location}
            reserveBottom={reserveBottom}
          >
            {renderPaneContent(isActive)}
          </KeepAlivePane>
        );
      })}
    </>
  );
};

MobileTabPanes.propTypes = {
  routes: PropTypes.array.isRequired,
  isAccessGranted: PropTypes.bool,
  isResolvingPermissions: PropTypes.bool,
  reserveBottom: PropTypes.bool,
};

export default MobileTabPanes;
